import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../api";
import { getCurrentCustomer } from "../../utils/auth";
import { useSettings } from "../../context/SettingsContext";
import { useTranslation } from "react-i18next";

const ReturnRequest = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currencySymbol } = useSettings();

  const [order, setOrder] = useState(null);
  const [existingReturns, setExistingReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Form states
  const [orderItemId, setOrderItemId] = useState(""); // empty means whole order
  const [reason, setReason] = useState("defective");
  const [description, setDescription] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [previews, setPreviews] = useState([]);

  const fetchOrderAndReturns = useCallback(async () => {
    setLoading(true);
    setError("");
    const customer = getCurrentCustomer();
    if (!customer?.id) {
      setError(t("return.loginRequired", "Please login to request a return."));
      setLoading(false);
      return;
    }

    try {
      // 1. Fetch Order details
      const orderRes = await api.get(`/orders/${orderId}`);
      const fetchedOrder = orderRes?.data?.data;
      setOrder(fetchedOrder);

      if (fetchedOrder && fetchedOrder.status !== "delivered") {
        setError(t("return.notDelivered", "Returns can only be requested for delivered orders."));
      }

      // 2. Fetch any existing return requests for this order
      const returnRes = await api.get("/return-requests/my", {
        params: { order_id: orderId }
      });
      setExistingReturns(returnRes?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || t("return.fetchError", "Failed to load order information."));
    } finally {
      setLoading(false);
    }
  }, [orderId, t]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchOrderAndReturns();
  }, [fetchOrderAndReturns]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(files);

    // Create preview URLs
    const previewUrls = files.map((file) => URL.createObjectURL(file));
    setPreviews(previewUrls);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccessMsg("");

    const customer = getCurrentCustomer();
    const formData = new FormData();
    formData.append("order_id", orderId);
    if (orderItemId) {
      formData.append("order_item_id", orderItemId);
    }
    formData.append("customer_id", customer?.id || "");
    formData.append("reason", reason);
    formData.append("description", description);
    formData.append("created_by", "customer");

    selectedImages.forEach((img) => {
      formData.append("images[]", img);
    });

    try {
      await api.post("/return-requests", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setSuccessMsg(t("return.submitSuccess", "Return request submitted successfully. We will review it shortly."));
      setSelectedImages([]);
      setPreviews([]);
      setDescription("");
      // Refresh list
      const returnRes = await api.get("/return-requests/my", {
        params: { order_id: orderId }
      });
      setExistingReturns(returnRes?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || t("return.submitError", "Failed to submit return request."));
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      reviewing: "bg-blue-100 text-blue-800 border-blue-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
      received: "bg-purple-100 text-purple-800 border-purple-200",
      refunded: "bg-emerald-100 text-emerald-800 border-emerald-200",
      completed: "bg-gray-100 text-gray-800 border-gray-200",
    };
    const c = colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200";
    return (
      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border capitalize ${c}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-10 px-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-4 bg-gray-200 rounded w-2/4" />
          <div className="h-64 bg-gray-200 rounded-lg w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
          <Link to="/user/my-orders" className="hover:text-emerald-600 transition">
            {t("return.myOrders", "My Orders")}
          </Link>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-gray-800 font-medium">{t("return.requestReturn", "Request Return")}</span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 text-sm flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {successMsg && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg mb-6 text-sm flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {successMsg}
          </div>
        )}

        {/* Existing requests list */}
        {existingReturns.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-8">
            <h2 className="font-bold text-lg text-gray-800 mb-4">{t("return.existingRequests", "Your Return Requests for this Order")}</h2>
            <div className="space-y-4">
              {existingReturns.map((ret) => (
                <div key={ret.id} className="border border-gray-100 rounded-lg p-4 bg-gray-50 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold text-gray-800">{ret.return_number}</span>
                      {getStatusBadge(ret.status)}
                    </div>
                    <p className="text-xs text-gray-500">
                      {t("return.requestedOn", "Requested on")}: {new Date(ret.created_at).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">{t("return.itemLabel", "Item")}:</span>{" "}
                      {ret.order_item ? ret.order_item.product_name : t("return.entireOrder", "Entire Order")}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">{t("return.reasonLabel", "Reason")}:</span>{" "}
                      <span className="capitalize">{ret.reason?.replace(/_/g, " ")}</span>
                    </p>
                    {ret.description && (
                      <p className="text-sm text-gray-600 bg-white p-2.5 rounded border border-gray-100 text-xs italic">
                        "{ret.description}"
                      </p>
                    )}
                    {ret.admin_notes && (
                      <div className="p-3 bg-red-50 border border-red-100 rounded text-sm text-red-800">
                        <span className="font-bold text-xs uppercase block mb-1">{t("return.adminNotes", "Admin Response")}</span>
                        {ret.admin_notes}
                      </div>
                    )}
                  </div>
                  {ret.refund_amount > 0 && (
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs text-gray-500 block">{t("return.refundedAmount", "Refunded Amount")}</span>
                      <span className="text-lg font-bold text-emerald-600">
                        {currencySymbol} {Number(ret.refund_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Return submission form */}
        {order && order.status === "delivered" && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-emerald-50 px-6 py-4 border-b border-emerald-100">
              <h2 className="font-bold text-lg text-emerald-800">{t("return.newRequest", "Submit Return Request")}</h2>
              <p className="text-xs text-emerald-600 mt-0.5">
                {t("return.orderNum", "Order Number")}: #{order.order_number || order.id} | {t("return.deliveredDate", "Delivered Date")}: {new Date(order.updated_at).toLocaleDateString()}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Select Return Item */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("return.selectItem", "What would you like to return?")}
                </label>
                <select
                  value={orderItemId}
                  onChange={(e) => setOrderItemId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white text-gray-800 cursor-pointer"
                >
                  <option value="">{t("return.entireOrderOption", "Entire Order (All Items)")}</option>
                  {order.items?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.product_name} (Qty: {item.quantity}) - {currencySymbol} {Number(item.total_price || 0).toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Reason */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("return.reason", "Reason for return")}
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white text-gray-800 cursor-pointer"
                >
                  <option value="defective">{t("return.reasons.defective", "Defective/Faulty Item")}</option>
                  <option value="wrong_item">{t("return.reasons.wrong_item", "Wrong Item Received")}</option>
                  <option value="not_as_described">{t("return.reasons.not_as_described", "Item not as Described")}</option>
                  <option value="changed_mind">{t("return.reasons.changed_mind", "Changed My Mind")}</option>
                  <option value="damaged_in_transit">{t("return.reasons.damaged_transit", "Damaged in Transit")}</option>
                  <option value="other">{t("return.reasons.other", "Other Reason")}</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("return.comments", "Details or Comments")}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="4"
                  required
                  placeholder={t("return.commentsPlaceholder", "Please explain details about the item's issue...")}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-gray-800 text-sm"
                />
              </div>

              {/* Image upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("return.uploadImages", "Upload Photos (Evidence of defect or item condition)")}
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer"
                />
                <p className="text-xs text-gray-400 mt-2">
                  {t("return.uploadNotice", "You can upload multiple images. Formats: JPEG, PNG, WEBP. Max size: 2MB per photo.")}
                </p>

                {previews.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-4">
                    {previews.map((url, i) => (
                      <div key={i} className="relative w-20 h-20 border rounded-lg overflow-hidden shadow-sm">
                        <img src={url} alt="preview" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit button */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold text-sm transition disabled:opacity-55 flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t("return.submitting", "Submitting...")}
                    </>
                  ) : (
                    t("return.submitBtn", "Submit Return Request")
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/user/my-orders")}
                  className="px-6 py-2.5 border border-gray-200 hover:bg-gray-50 rounded-lg text-gray-600 text-sm font-semibold transition cursor-pointer"
                >
                  {t("return.cancel", "Cancel")}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReturnRequest;
